/**
 * TrendChart — Pure SVG line chart for historical data.
 * No external dependencies.
 *
 * Props:
 *   data   — Array of { date: ISO string, value: number }
 *            OR for range mode: Array of { date, low: number, high: number }
 *   color  — Stroke/fill color (CSS variable or hex)
 *   colorHigh — Optional second color for the high line in range mode
 *   height — Chart height in px (default 100)
 *   label  — Y-axis label (e.g. "$" or "pts")
 *   formatValue — Optional function to format the value label (default: String)
 *   rangeMode — Boolean: true = dual-line (low/high) with shaded band
 */

const PADDING = { top: 10, right: 12, bottom: 24, left: 6 };

function formatMonth(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short' });
  } catch {
    return '';
  }
}

export default function TrendChart({
  data,
  color = 'var(--accent)',
  colorHigh,
  height = 100,
  label = '',
  formatValue,
  rangeMode = false,
}) {
  const fmt = formatValue || String;

  if (!data || data.length < 2) {
    return (
      <div
        style={{
          padding: '16px 0',
          textAlign: 'center',
          fontSize: '0.75rem',
          color: 'var(--text-muted)',
        }}
      >
        Not enough data yet — check back after your next analysis.
      </div>
    );
  }

  const width = 300; // viewBox width, scales to container
  const chartW = width - PADDING.left - PADDING.right;
  const chartH = height - PADDING.top - PADDING.bottom;

  // Compute Y-axis range depending on mode
  let allValues;
  if (rangeMode) {
    allValues = data.flatMap((d) => [d.low, d.high]).filter((v) => typeof v === 'number');
  } else {
    allValues = data.map((d) => d.value).filter((v) => typeof v === 'number');
  }

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = max - min || 1;
  const pad = range * 0.1;
  const yMin = min - pad;
  const yMax = max + pad;
  const yRange = yMax - yMin;

  function x(i) {
    return PADDING.left + (i / (data.length - 1)) * chartW;
  }
  function y(v) {
    return PADDING.top + chartH - ((v - yMin) / yRange) * chartH;
  }

  // Date labels — show at most 4 evenly spaced
  const labelCount = Math.min(data.length, 4);
  const dateLabels = [];
  for (let i = 0; i < labelCount; i++) {
    const idx = Math.round((i / (labelCount - 1)) * (data.length - 1));
    dateLabels.push({ idx, label: formatMonth(data[idx].date) });
  }

  const gradientId = `trend-grad-${Math.random().toString(36).slice(2, 6)}`;
  const bandGradientId = `trend-band-${Math.random().toString(36).slice(2, 6)}`;

  /* ─── Range mode (dual-line with shaded band) ─── */
  if (rangeMode) {
    const lowColor = color;
    const highColor = colorHigh || 'var(--success)';

    const lowPoints = data.map((d, i) => `${x(i)},${y(d.low)}`).join(' ');
    const highPoints = data.map((d, i) => `${x(i)},${y(d.high)}`).join(' ');

    // Shaded band polygon: high line forward, low line reversed
    const bandPoints = [
      ...data.map((d, i) => `${x(i)},${y(d.high)}`),
      ...data.map((d, i) => `${x(data.length - 1 - i)},${y(data[data.length - 1 - i].low)}`),
    ].join(' ');

    const lastLow = data[data.length - 1].low;
    const lastHigh = data[data.length - 1].high;

    return (
      <div style={{ marginTop: 12 }}>
        {label && (
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: 4 }}>
            {label}
          </div>
        )}
        <svg
          viewBox={`0 0 ${width} ${height}`}
          style={{ width: '100%', height: 'auto', display: 'block' }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id={bandGradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={highColor} stopOpacity="0.12" />
              <stop offset="100%" stopColor={lowColor} stopOpacity="0.06" />
            </linearGradient>
          </defs>

          {/* Shaded band between high and low */}
          <polygon points={bandPoints} fill={`url(#${bandGradientId})`} />

          {/* High line */}
          <polyline
            points={highPoints}
            fill="none"
            stroke={highColor}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Low line */}
          <polyline
            points={lowPoints}
            fill="none"
            stroke={lowColor}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeDasharray="4 3"
          />

          {/* Data points — high */}
          {data.map((d, i) => (
            <circle
              key={`h-${i}`}
              cx={x(i)}
              cy={y(d.high)}
              r={i === data.length - 1 ? 4 : 2.5}
              fill={highColor}
              stroke="var(--bg-card)"
              strokeWidth={i === data.length - 1 ? 2 : 1}
            />
          ))}

          {/* Data points — low */}
          {data.map((d, i) => (
            <circle
              key={`l-${i}`}
              cx={x(i)}
              cy={y(d.low)}
              r={i === data.length - 1 ? 4 : 2.5}
              fill={lowColor}
              stroke="var(--bg-card)"
              strokeWidth={i === data.length - 1 ? 2 : 1}
            />
          ))}

          {/* Current value callouts */}
          <text
            x={x(data.length - 1)}
            y={y(lastHigh) - 8}
            textAnchor="end"
            style={{ fontSize: '10px', fontWeight: 700, fill: highColor }}
          >
            {fmt(lastHigh)}
          </text>
          <text
            x={x(data.length - 1)}
            y={y(lastLow) + 14}
            textAnchor="end"
            style={{ fontSize: '10px', fontWeight: 700, fill: lowColor }}
          >
            {fmt(lastLow)}
          </text>

          {/* Date labels */}
          {dateLabels.map((dl) => (
            <text
              key={dl.idx}
              x={x(dl.idx)}
              y={height - 4}
              textAnchor="middle"
              style={{ fontSize: '8px', fill: 'var(--text-muted, #888)' }}
            >
              {dl.label}
            </text>
          ))}
        </svg>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 16, height: 2, background: highColor, borderRadius: 1 }} />
            <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>High</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 16, height: 2, background: lowColor, borderRadius: 1, borderTop: '1px dashed ' + lowColor }} />
            <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Low</span>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Single-line mode (original) ─── */
  const points = data.map((d, i) => `${x(i)},${y(d.value)}`).join(' ');
  const areaPoints = [
    `${x(0)},${y(data[0].value)}`,
    ...data.map((d, i) => `${x(i)},${y(d.value)}`),
    `${x(data.length - 1)},${PADDING.top + chartH}`,
    `${x(0)},${PADDING.top + chartH}`,
  ].join(' ');

  const lastValue = data[data.length - 1].value;

  return (
    <div style={{ marginTop: 12 }}>
      {label && (
        <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginBottom: 4 }}>
          {label}
        </div>
      )}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Area fill */}
        <polygon points={areaPoints} fill={`url(#${gradientId})`} />

        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Data points */}
        {data.map((d, i) => (
          <circle
            key={i}
            cx={x(i)}
            cy={y(d.value)}
            r={i === data.length - 1 ? 4 : 2.5}
            fill={color}
            stroke="var(--bg-card)"
            strokeWidth={i === data.length - 1 ? 2 : 1}
          />
        ))}

        {/* Current value callout */}
        <text
          x={x(data.length - 1)}
          y={y(lastValue) - 8}
          textAnchor="end"
          style={{ fontSize: '10px', fontWeight: 700, fill: color }}
        >
          {fmt(lastValue)}
        </text>

        {/* Date labels */}
        {dateLabels.map((dl) => (
          <text
            key={dl.idx}
            x={x(dl.idx)}
            y={height - 4}
            textAnchor="middle"
            style={{ fontSize: '8px', fill: 'var(--text-muted, #888)' }}
          >
            {dl.label}
          </text>
        ))}
      </svg>
    </div>
  );
}
